/**
 * System status → Email (1.0.0-rc.4): whether email is set up (PRISM_SMTP_* in .env; never edited here, and the
 * password is never shown), the send queue, the last error, the latest mails, and Send test email.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Mail } from 'lucide-react';
import { api, errorMessage } from '../../../api/client';
import { useMe } from '../../../api/hooks';
import { toast } from '../../../components/Toasts';
import { TableScroll } from '../../../components/TableScroll';
import { relative } from '../../../lib/format';

interface EmailDTO {
  configured: boolean; host: string | null; port: number; secure: string; from: string | null; authenticated: boolean;
  queue: { pending: number; failed24h: number; sent24h: number };
  lastError: { at: string; message: string } | null; lastSuccess: { at: string } | null;
  recent: Array<{ id: string; to: string; kind: string; subject: string; status: string; attempts: number; lastError: string | null; createdAt: string; sentAt: string | null }>;
}

const KIND: Record<string, string> = {
  test: 'Test', password_reset: 'Password reset', password_changed: 'Password changed', approval_requested: 'Approval requested',
  approval_decided: 'Approval decided', task_assigned: 'Task assigned', collection_shared: 'Collection shared', system_alerts: 'System status alerts',
};

export function EmailStatus() {
  const me = useMe().data!;
  const q = useQuery({ queryKey: ['system-email'], queryFn: () => api<EmailDTO>('/api/v1/system/email'), refetchInterval: 10_000 });
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const d = q.data;
  async function send() {
    setBusy(true);
    try {
      const r = await api<{ status: string; to: string; error?: string }>('/api/v1/system/email/test', { method: 'POST', json: to.trim() ? { to: to.trim() } : {} });
      if (r.status === 'sent') toast(`Test email sent to ${r.to}`);
      else if (r.status === 'queued') toast(`Test email to ${r.to} is queued; it is still being sent`);
      else toast(`Not sent: ${r.error ?? r.status}`, 'error');
      await q.refetch();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  if (!d) return <div className="skeleton" style={{ height: 160 }} />;
  return (
    <section className="panel-card" style={{ maxWidth: 860 }} data-testid="email-status">
      <p className="section-label">Email</p>
      {!d.configured ? (
        <div className="banner info" data-testid="email-not-configured"><div className="banner-body"><strong>Email is not set up</strong>
          <span>PRISM sends no email; everything is in the app. To send password reset links and notifications, set PRISM_SMTP_HOST, PRISM_SMTP_FROM and the rest in .env and restart (docs/deployment.md, “Email”).</span></div></div>
      ) : (
        <dl className="kv" data-testid="email-config">
          <dt>Server</dt><dd className="mono">{d.host}:{d.port} · {d.secure === 'starttls' ? 'STARTTLS' : d.secure === 'tls' ? 'TLS' : 'no encryption'}</dd>
          <dt>From</dt><dd>{d.from}</dd>
          <dt>Sign-in</dt><dd>{d.authenticated ? 'User name and password (from .env)' : 'None'}</dd>
          <dt>Queue</dt><dd>{d.queue.pending} waiting · {d.queue.sent24h} sent and {d.queue.failed24h} failed in the last 24 hours</dd>
          <dt>Last sent</dt><dd>{d.lastSuccess ? relative(d.lastSuccess.at) : 'Nothing yet'}</dd>
        </dl>
      )}
      {d.lastError && <div className="banner error" data-testid="email-last-error"><div className="banner-body"><strong>Last error, {relative(d.lastError.at)}</strong><span className="mono" style={{ fontSize: 12 }}>{d.lastError.message}</span></div></div>}
      {d.configured && (
        <div className="field"><label htmlFor="email-test-to">Send test email</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input id="email-test-to" className="input" type="email" placeholder={me.user.email} value={to} onChange={(e) => setTo(e.target.value)} style={{ maxWidth: 320 }} />
            <button className="btn primary" disabled={busy} onClick={() => void send()} data-testid="send-test-email">{busy ? <Loader2 size={14} className="spin" /> : <Mail size={14} />} Send test email</button>
          </div>
          <span className="help">To you unless you enter another address. It goes through the queue, as every mail does.</span></div>
      )}
      {d.recent.length > 0 && (
        <TableScroll>
        <table className="table compact" data-testid="email-recent">
          <thead><tr><th>Queued</th><th>To</th><th>What</th><th>State</th></tr></thead>
          <tbody>{d.recent.map((m) => (
            <tr key={m.id}><td className="muted nowrap">{relative(m.createdAt)}</td><td className="wrap">{m.to}</td><td className="wrap">{KIND[m.kind] ?? m.kind}</td>
              <td className="wrap" style={{ minWidth: 160 }}><span className={`pill ${m.status === 'sent' ? 'accent' : m.status === 'failed' ? 'danger' : 'warning'}`}>{m.status}</span>{m.attempts > 1 && <span className="faint"> · {m.attempts} tries</span>}
                {m.lastError && m.status !== 'sent' && <div className="faint" style={{ fontSize: 11.5 }}>{m.lastError}</div>}</td></tr>
          ))}</tbody>
        </table>
        </TableScroll>
      )}
    </section>
  );
}
